/***************************************************************************************
 * FileName      : QuadTextPlacementManager.ts
 * Description   : 4개 꼭짓점 좌표 기반 텍스트 배치 관리 서비스
 *                 - mp.quadTextPlane 컴포넌트 사용
 *                 - sdk.Scene.createNode() + node.addComponent() + node.start() 패턴
 *                 - 텍스트 추가, 제거, 업데이트
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : matterport_mvp1/src/09-text-quad-placement/src/services/QuadTextPlacementManager.ts
 ***************************************************************************************/

import { initComponents } from '@mp/common';
import { computeQuadCenter, cornersToLocal, DEFAULT_TEXTURE_SIZE, QuadCorners } from '../types/quad-image';
import {
  QuadTextPlacement,
  QuadTextNodeInfo,
  DEFAULT_TEXT_STYLE,
} from '../types/quad-text';

// ============================================================================
// QuadTextPlacementManager 클래스
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 4개 꼭짓점 좌표 기반 텍스트 배치 관리 서비스
 *
 * 접근 방식:
 *   1. 4개 꼭짓점의 중심점을 노드 위치로 설정
 *   2. 꼭짓점을 중심 기준 로컬 좌표로 변환
 *   3. mp.quadTextPlane 컴포넌트가 로컬 좌표로 BufferGeometry 생성
 *   4. 텍스트를 해당 사각형에 정밀하게 렌더링
 */
export class QuadTextPlacementManager {
  /** Matterport SDK 인스턴스 */
  private sdk: any = null;

  /** 텍스트 ID → 노드 정보 매핑 */
  private textNodes: Map<string, QuadTextNodeInfo> = new Map();

  /** 초기화 완료 여부 */
  private initialized: boolean = false;

  // ==========================================================================
  // 2026.04.23 : 박은정 : 캔버스 비율 계산 헬퍼
  // ==========================================================================

  /**
   * 2026.04.23 : 박은정 : 4꼭짓점으로부터 월드 좌표 기준 width/height를 구해
   *   캔버스가 플레인과 동일한 비율을 갖도록 텍스처 크기를 계산.
   * - 기본: 긴 변 = 1024 (DEFAULT_TEXTURE_SIZE.w)
   * - fontSize가 짧은 변보다 크면, 텍스트가 들어갈 수 있게 캔버스를 비율 유지한 채 확대
   *   → fontSize 에 상한(잘림)이 생기지 않도록 보장
   * - 최종 캔버스는 4096 × 4096 까지 허용 (WebGL 한계 내)
   */
  private computeTextureSize(
    corners: QuadCorners,
    fontSize: number = 100,
    lineHeight: number = 1.2,
    numLines: number = 1,
    padding: number = 8,
  ): { w: number; h: number } {
    const { topLeft: TL, topRight: TR, bottomLeft: BL } = corners;
    const wMeter = Math.hypot(TR.x - TL.x, TR.y - TL.y, TR.z - TL.z);
    const hMeter = Math.hypot(BL.x - TL.x, BL.y - TL.y, BL.z - TL.z);
    if (wMeter <= 0 || hMeter <= 0) return { ...DEFAULT_TEXTURE_SIZE };

    const MAX = DEFAULT_TEXTURE_SIZE.w;
    let w: number;
    let h: number;
    if (wMeter >= hMeter) {
      w = MAX;
      h = Math.round(MAX * (hMeter / wMeter));
    } else {
      h = MAX;
      w = Math.round(MAX * (wMeter / hMeter));
    }

    const requiredH = Math.ceil(Math.max(1, numLines) * fontSize * lineHeight + padding * 2);
    if (h < requiredH) {
      const scale = requiredH / h;
      h = Math.ceil(h * scale);
      w = Math.ceil(w * scale);
    }

    const HARD_MAX = 4096;
    if (w > HARD_MAX || h > HARD_MAX) {
      const scale = Math.min(HARD_MAX / w, HARD_MAX / h);
      w = Math.floor(w * scale);
      h = Math.floor(h * scale);
    }
    return { w: Math.max(1, w), h: Math.max(1, h) };
  }

  // ==========================================================================
  // 초기화
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : SDK 초기화 및 컴포넌트 등록
   * @param sdk Matterport SDK 인스턴스
   */
  async initialize(sdk: any): Promise<void> {
    this.sdk = sdk;

    // 2026.02.12 : 박은정 : SDK 컴포넌트 등록 (mp.quadTextPlane 포함)
    await initComponents(sdk);

    this.initialized = true;
  }

  // ==========================================================================
  // 텍스트 추가
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : 4개 꼭짓점 좌표로 텍스트 배치
   *
   * 처리 순서:
   *   1. 4개 꼭짓점의 중심점 계산
   *   2. 꼭짓점을 중심 기준 로컬 좌표로 변환
   *   3. sdk.Scene.createNode() → 노드 위치를 중심점으로 설정
   *   4. node.addComponent('mp.quadTextPlane', { 로컬 꼭짓점 좌표, 텍스트 설정 })
   *   5. node.start()
   *
   * @param placement 텍스트 배치 설정 (4개 꼭짓점 포함)
   * @returns 생성된 텍스트 노드 정보
   */
  async addText(placement: QuadTextPlacement): Promise<QuadTextNodeInfo> {
    if (!this.initialized || !this.sdk) {
      throw new Error('[QuadTextPlacementManager] SDK가 초기화되지 않았습니다.');
    }

    // 2026.02.12 : 박은정 : 중복 ID 체크
    // 2026.04.27 : 박은정 : 동일 ID 재진입 시 로그로 호출 경로 추적 (이중 라벨 원인 진단용)
    if (this.textNodes.has(placement.id)) {
      console.warn(`[QuadTextPlacementManager] addText 중복 ID: ${placement.id} → 기존 노드 제거 후 재생성`);
      await this.removeText(placement.id);
    }

    // 2026.02.12 : 박은정 : 중심점 계산
    const center = computeQuadCenter(placement.corners);

    // 2026.02.12 : 박은정 : 로컬 좌표 변환
    const localCorners = cornersToLocal(placement.corners, center);

    // 2026.02.12 : 박은정 : 텍스트 스타일
    const style = placement.textStyle || DEFAULT_TEXT_STYLE;

    // 2026.02.12 : 박은정 : 텍스처 크기
    // 2026.04.23 : 박은정 : 플레인 비율 + fontSize 에 맞춰 캔버스 크기 계산
    //   (fontSize 가 커져도 캔버스가 따라 커져 상하 잘림 방지)
    const numLines = Math.max(1, (placement.text || '').split('\n').length);
    const textureSize = placement.textureSize || this.computeTextureSize(
      placement.corners, style.fontSize, style.lineHeight, numLines, style.padding,
    );

    // 2026.02.12 : 박은정 : Scene Node 생성
    const node = await this.sdk.Scene.createNode();

    // 2026.02.12 : 박은정 : 노드 위치를 중심점으로 설정
    node.position.set(center.x, center.y, center.z);

    // 2026.02.12 : 박은정 : mp.quadTextPlane 컴포넌트 추가 (로컬 좌표 전달)
    const component = node.addComponent('mp.quadTextPlane', {
      text: placement.text,
      fontSize: style.fontSize,
      fontFamily: style.fontFamily,
      fontWeight: style.fontWeight,
      textColor: style.textColor,
      backgroundColor: style.backgroundColor,
      textAlign: style.textAlign,
      lineHeight: style.lineHeight,
      padding: style.padding,
      textureWidth: textureSize.w,
      textureHeight: textureSize.h,
      topLeft: localCorners.topLeft,
      topRight: localCorners.topRight,
      bottomLeft: localCorners.bottomLeft,
      bottomRight: localCorners.bottomRight,
      transparent: true,
      opacity: placement.opacity,
      visible: placement.visible,
      // 2026.02.12 : 박은정 : 테두리 표시 여부 (라벨은 false)
      showBorder: placement.showBorder !== false,
      doubleSide: true,
    });

    // 2026.02.12 : 박은정 : node.start() → onInit() 호출
    node.start();

    // 2026.02.12 : 박은정 : 노드 정보 저장
    const nodeInfo: QuadTextNodeInfo = {
      id: placement.id,
      placement: { ...placement },
      node,
      component,
    };

    this.textNodes.set(placement.id, nodeInfo);

    return nodeInfo;
  }

  // ==========================================================================
  // 텍스트 제거
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : 특정 텍스트 제거
   * @param id 텍스트 ID
   */
  async removeText(id: string): Promise<void> {
    const nodeInfo = this.textNodes.get(id);
    if (nodeInfo) {
      nodeInfo.node.stop();
    }
    this.textNodes.delete(id);
  }

  /**
   * 2026.02.12 : 박은정 : 모든 텍스트 제거
   */
  async removeAllTexts(): Promise<void> {
    const ids = Array.from(this.textNodes.keys());
    for (const id of ids) {
      await this.removeText(id);
    }
  }

  // ==========================================================================
  // 텍스트 업데이트
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : 텍스트 속성 업데이트
   * @param id 텍스트 ID
   * @param updates 업데이트할 속성
   */
  updateText(id: string, updates: Partial<QuadTextPlacement>): void {
    const nodeInfo = this.textNodes.get(id);
    if (!nodeInfo) {
      return;
    }

    const { component, placement } = nodeInfo;

    // 2026.02.12 : 박은정 : 텍스트 내용 업데이트
    if (updates.text !== undefined) {
      component.inputs.text = updates.text;
      placement.text = updates.text;
    }

    // 2026.02.12 : 박은정 : 텍스트 스타일 업데이트
    if (updates.textStyle) {
      const style = updates.textStyle;
      if (style.fontSize !== undefined) {
        component.inputs.fontSize = style.fontSize;
      }
      if (style.fontFamily !== undefined) {
        component.inputs.fontFamily = style.fontFamily;
      }
      if (style.fontWeight !== undefined) {
        component.inputs.fontWeight = style.fontWeight;
      }
      if (style.textColor !== undefined) {
        component.inputs.textColor = style.textColor;
      }
      if (style.backgroundColor !== undefined) {
        component.inputs.backgroundColor = style.backgroundColor;
      }
      if (style.textAlign !== undefined) {
        component.inputs.textAlign = style.textAlign;
      }
      if (style.lineHeight !== undefined) {
        component.inputs.lineHeight = style.lineHeight;
      }
      if (style.padding !== undefined) {
        component.inputs.padding = style.padding;
      }
      placement.textStyle = { ...placement.textStyle, ...style };
    }

    // 2026.02.12 : 박은정 : 투명도 업데이트
    if (updates.opacity !== undefined) {
      component.inputs.opacity = updates.opacity;
      placement.opacity = updates.opacity;
    }

    // 2026.02.12 : 박은정 : 표시 여부 업데이트
    if (updates.visible !== undefined) {
      component.inputs.visible = updates.visible;
      placement.visible = updates.visible;
    }

    // 2026.02.12 : 박은정 : 꼭짓점 좌표 업데이트 (전체 재배치)
    if (updates.corners) {
      const center = computeQuadCenter(updates.corners);
      const localCorners = cornersToLocal(updates.corners, center);

      nodeInfo.node.position.set(center.x, center.y, center.z);
      component.inputs.topLeft = localCorners.topLeft;
      component.inputs.topRight = localCorners.topRight;
      component.inputs.bottomLeft = localCorners.bottomLeft;
      component.inputs.bottomRight = localCorners.bottomRight;

      placement.corners = { ...updates.corners };
    }

    // 2026.04.23 : 박은정 : corners / text / fontSize / padding / lineHeight 변경 시
    //   캔버스 크기 재계산. fontSize 가 커져도 세로가 따라 커져 잘리지 않게 함.
    if (!placement.textureSize) {
      const s = placement.textStyle || DEFAULT_TEXT_STYLE;
      const numLines = Math.max(1, (placement.text || '').split('\n').length);
      const newSize = this.computeTextureSize(
        placement.corners, s.fontSize, s.lineHeight, numLines, s.padding,
      );
      if (
        newSize.w !== component.inputs.textureWidth ||
        newSize.h !== component.inputs.textureHeight
      ) {
        component.inputs.textureWidth = newSize.w;
        component.inputs.textureHeight = newSize.h;
      }
    }
  }

  // ==========================================================================
  // 조회
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : 배치된 텍스트 수
   */
  getTextCount(): number {
    return this.textNodes.size;
  }

  /**
   * 2026.02.12 : 박은정 : 초기화 상태 확인
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 2026.02.12 : 박은정 : 특정 텍스트 노드 정보 조회
   */
  getTextNode(id: string): QuadTextNodeInfo | undefined {
    return this.textNodes.get(id);
  }

  /**
   * 2026.02.12 : 박은정 : 모든 텍스트 노드 정보 조회
   */
  getAllTextNodes(): QuadTextNodeInfo[] {
    return Array.from(this.textNodes.values());
  }
}

// ============================================================================
// 싱글톤 인스턴스
// ============================================================================

// 2026.02.12 : 박은정 : 전역 싱글톤 인스턴스
let managerInstance: QuadTextPlacementManager | null = null;

/**
 * 2026.02.12 : 박은정 : QuadTextPlacementManager 싱글톤 인스턴스 반환
 */
export function getQuadTextPlacementManager(): QuadTextPlacementManager {
  if (!managerInstance) {
    managerInstance = new QuadTextPlacementManager();
  }
  return managerInstance;
}

